/**
 * @fileoverview Instructor-side "Assignments" card on the student profile
 * (Spec 01): open assignments with live progress, plus a collapsed history
 * of completed/closed ones.
 *
 * "Assign practice" lives here (iOS review B5): it picks an item from the
 * student's recent personal lessons, which the page passes in as `lessons`.
 * The card shows whenever there is something to list OR something to assign,
 * so the button is reachable before the first assignment exists.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { assignableLessons, assignmentPillKind, assignmentProgressLabel } from "@/lib/assignments";
import { AssignPracticeDialog } from "@/components/AssignPracticeDialog";
import { Card, Icon, SectionHeader, StatusPill } from "@/components/studio/primitives";

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const AssignmentRow = ({ assignment, borderBottom }) => (
  <div
    className={`flex items-start gap-3 px-[18px] py-3.5 ${borderBottom ? "border-b border-[var(--studio-divider)]" : ""}`}
    data-testid={`assignment-row-${assignment.assignment_id}`}
  >
    <div className="min-w-0 flex-1">
      <p className="font-sans text-sm text-[var(--text-primary)]">{assignment.text}</p>
      <p className="mt-1 font-mono text-[12px] text-[var(--text-tertiary)]">
        {[assignmentProgressLabel(assignment), formatDate(assignment.created_at)].filter(Boolean).join(" · ")}
      </p>
    </div>
    <StatusPill kind={assignmentPillKind(assignment)} className="shrink-0" />
  </div>
);

const NO_LESSONS = [];

export function StudentAssignmentsCard({ studentId, lessons = NO_LESSONS, rehearsalsOn = true }) {
  const [assignments, setAssignments] = useState([]);
  // False until the list loads, and after a refusal (the server's own
  // assignments flag off): nothing to show, and nothing to assign into.
  const [loaded, setLoaded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const groups = useMemo(() => assignableLessons(lessons), [lessons]);

  const load = useCallback(
    async (signal) => {
      try {
        const res = await api.get(`/students/${studentId}/assignments`, { signal });
        setAssignments(res.data || []);
        setLoaded(true);
      } catch {
        /* A refused first load keeps the card hidden; a failed refresh keeps
           the list it already shows. */
      }
    },
    [studentId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setAssignments([]);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (!loaded || (assignments.length === 0 && groups.length === 0)) return null;

  const open = assignments.filter((a) => a.status === "open");
  const history = assignments.filter((a) => a.status !== "open");

  return (
    <div data-testid="student-assignments-card">
      <SectionHeader
        action={groups.length > 0 ? "Assign practice" : null}
        onAction={() => setAssignOpen(true)}
        actionTestId="assign-practice-button"
      >
        Assignments
      </SectionHeader>
      <Card padding={0}>
        {open.length === 0 ? (
          <p className="px-[18px] py-4 font-sans text-sm text-[var(--text-tertiary)]">Nothing assigned right now</p>
        ) : (
          open.map((a, i) => (
            <AssignmentRow key={a.assignment_id} assignment={a} borderBottom={i < open.length - 1 || history.length > 0} />
          ))
        )}
        {history.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex w-full items-center gap-2 px-[18px] py-3 font-sans text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
              data-testid="assignments-history-toggle"
              aria-expanded={historyOpen}
            >
              <Icon name={historyOpen ? "chevronUp" : "chevronDown"} size={14} />
              History ({history.length})
            </button>
            {historyOpen &&
              history.map((a, i) => (
                <AssignmentRow key={a.assignment_id} assignment={a} borderBottom={i < history.length - 1} />
              ))}
          </>
        )}
      </Card>
      <AssignPracticeDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        groups={groups}
        assignments={assignments}
        rehearsalsOn={rehearsalsOn}
        onAssigned={() => load()}
      />
    </div>
  );
}
